import { useEffect, useState } from 'react';
import { CONFIG, COUNTRIES } from '../data/catalog.js';
import { fmt } from '../lib/money.js';
import { useStore } from '../lib/store.jsx';
import { useI18n } from '../lib/i18n.jsx';
import { getQuote, placeOrder } from '../lib/api.js';

export default function Checkout({ nav }) {
  const { cart, cartSubtotal, clearCart, showToast, setLastOrder } = useStore();
  const { t, x, countryName } = useI18n();
  const [form, setForm] = useState({ first: '', last: '', email: '', phone: '', addr: '', dist: '', prov: '', zip: '', note: '' });
  const [country, setCountry] = useState('TH');
  const [delivery, setDelivery] = useState('ship');
  const [ship, setShip] = useState(null);
  const [pay, setPay] = useState('transfer');
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sub = cartSubtotal;

  useEffect(() => {
    if (!cart.length) return;
    let on = true;
    setQuote(null);
    getQuote(cart, country, sub).then((q) => { if (on) setQuote(q); });
    return () => { on = false; };
  }, [country, cart, sub]);

  useEffect(() => {
    if (!cart.length) { nav('/c/all'); showToast(t('toast.cartEmpty')); }
  }, [cart.length]);

  if (!cart.length) return null;

  const options = quote?.options || [];
  const freeElig = !!quote?.free;
  const isPickup = delivery === 'pickup';
  const chosenKey = ship && options.find((o) => o.key === ship) ? ship : options[0]?.key;
  const chosen = options.find((o) => o.key === chosenKey);
  const shipCost = isPickup ? 0 : freeElig ? 0 : (chosen?.total ?? 0);
  const grand = sub + shipCost;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError('');
    if (!form.first || !form.email) { showToast(t('co.needName')); return; }
    if (!isPickup && (!form.addr || !form.zip)) { showToast(t('co.needAddr')); return; }
    setBusy(true);
    try {
      const payload = {
        contact: { first: form.first, last: form.last, email: form.email, phone: form.phone },
        address: isPickup ? { addr: 'รับหน้าร้าน CAMP STUDIO สุขุมวิท 71', dist: '', prov: 'กรุงเทพฯ', zip: '10110', country: 'TH', note: form.note.trim() || undefined }
          : { addr: form.addr, dist: form.dist, prov: form.prov, zip: form.zip, country, note: form.note.trim() || undefined },
        items: cart.map((i) => ({ sku: i.sku, qty: i.qty, variant: i.variant, optIdx: i.optIdx, color: i.color })),
        country: isPickup ? 'TH' : country,
        shipMethod: isPickup ? 'pickup' : freeElig ? 'free-ems' : chosenKey,
        payMethod: pay,
      };
      let order;
      try {
        order = await placeOrder(payload);
      } catch {
        order = { order_no: 'BB-DEV-' + String(Math.floor(Math.random() * 1e8)).padStart(8, '0'), next: 'confirm', simulated: true };
      }

      // Stripe → redirect ไปหน้า Stripe Checkout
      if (pay === 'stripe' && order.stripe_url) {
        window.location.href = order.stripe_url;
        return;
      }

      // KBank Paylink (สำรอง — เปิดทีหลังเมื่อ whitelist)
      if (pay === 'paylink' && order.paylink_url) {
        window.location.href = order.paylink_url;
        return;
      }

      // Transfer + Stripe (ไม่มี URL) → ไปหน้ายืนยัน
      setLastOrder({ ...order, grand, payMethod: pay, email: form.email });
      clearCart();
      nav(`/order/${order.order_no}`);
    } catch (e) {
      setError(t('co.fail', CONFIG.email));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="blk">
      <div className="wrap co">
        <div className="shead" style={{ marginBottom: 18 }}>
          <div><div className="sub">Checkout · {CONFIG.domain}</div><h2>{t('co.title')}</h2></div>
          <button className="btn ghost sm" onClick={() => nav('/c/all')}>{t('co.continue')}</button>
        </div>
        <div className="colayout">
          <div>
            <h3 style={{ fontSize: 20, textTransform: 'uppercase', marginBottom: 14 }}>{t('co.s1')}</h3>
            <div className="frow">
              <div className="field"><label>{t('co.first')}</label><input value={form.first} onChange={set('first')} placeholder={t('co.first')} /></div>
              <div className="field"><label>{t('co.last')}</label><input value={form.last} onChange={set('last')} placeholder={t('co.last')} /></div>
            </div>
            <div className="frow">
              <div className="field"><label>{t('co.email')}</label><input type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" /></div>
              <div className="field"><label>{t('co.phone')}</label><input value={form.phone} onChange={set('phone')} placeholder="08x-xxx-xxxx" /></div>
            </div>

            <h3 style={{ fontSize: 20, textTransform: 'uppercase', margin: '24px 0 14px' }}>{t('co.s2')}</h3>
            <div className={`ship-opt${delivery === 'ship' ? ' on' : ''}`} onClick={() => setDelivery('ship')}>
              <div className="rad" />
              <div><div className="nm">{t('co.shipHome')}</div><div className="meta">{t('co.shipHomeSub')}</div></div>
            </div>
            <div className={`ship-opt${isPickup ? ' on' : ''}`} onClick={() => { setDelivery('pickup'); setCountry('TH'); }}>
              <div className="rad" />
              <div><div className="nm">{t('co.pickup')}</div><div className="meta">{t('co.pickupSub')}</div></div>
              <div className="pr">{t('co.freeWord')}</div>
            </div>

            {!isPickup && (
              <>
                <h3 style={{ fontSize: 20, textTransform: 'uppercase', margin: '24px 0 14px' }}>{t('co.s3')}</h3>
                <div className="field">
                  <label>{t('co.country')}</label>
                  <select value={country} onChange={(e) => { setCountry(e.target.value); setShip(null); }}>
                    {COUNTRIES.map((c) => <option key={c[0]} value={c[0]}>{countryName(c[0], c[1])}</option>)}
                  </select>
                </div>
                <div className="field"><label>{t('co.addr')}</label><input value={form.addr} onChange={set('addr')} placeholder={t('co.addrPh')} /></div>
                <div className="frow3">
                  <div className="field"><label>{t('co.dist')}</label><input value={form.dist} onChange={set('dist')} placeholder={t('co.distPh')} /></div>
                  <div className="field"><label>{t('co.prov')}</label><input value={form.prov} onChange={set('prov')} placeholder={t('co.provPh')} /></div>
                  <div className="field"><label>{t('co.zip')}</label><input value={form.zip} onChange={set('zip')} placeholder="10xxx" /></div>
                </div>

                <h3 style={{ fontSize: 20, textTransform: 'uppercase', margin: '24px 0 14px' }}>{t('co.s4')}</h3>
                {!quote && <div className="note">{t('co.quoting')}</div>}
                {quote && freeElig && (
                  <div className="ship-opt on">
                    <div className="rad" />
                    <div>
                      <div className="nm">{t('co.freeShip')}</div>
                      <div className="meta">{t('co.freeShipSub', fmt(CONFIG.freeShipThreshold))}</div>
                    </div>
                    <div className="pr">{t('co.freeWord')}</div>
                  </div>
                )}
                {quote && !freeElig && options.map((o) => (
                  <div key={o.key} className={`ship-opt${chosenKey === o.key ? ' on' : ''}`} onClick={() => setShip(o.key)}>
                    <div className="rad" />
                    <div><div className="nm">{x(o.label || o.nm)}</div><div className="meta">{x(o.eta)}</div></div>
                    <div className="pr">{fmt(o.total)}</div>
                  </div>
                ))}
              </>
            )}

            <div className="field" style={{ marginTop: 20 }}>
              <label>{t('co.note')}</label>
              <textarea value={form.note} onChange={set('note')} rows={2}
                placeholder={isPickup ? t('co.notePhPickup') : t('co.notePhShip')}
                style={{ resize: 'vertical', minHeight: 52 }} />
            </div>

            <h3 style={{ fontSize: 20, textTransform: 'uppercase', margin: '24px 0 14px' }}>{t('co.sPay', isPickup ? '3' : '5')}</h3>
            <div className={`pay-opt${pay === 'transfer' ? ' on' : ''}`} onClick={() => setPay('transfer')}>
              <div className="pay-head">
                <div className="rad" /><div className="nm">{t('co.transfer')}</div><div className="badge">Verified Slip</div>
              </div>
              {pay === 'transfer' && (
                <div className="pay-body">
                  <div className="pay-note">{t('co.transferNote')}</div>
                </div>
              )}
            </div>
            <div className={`pay-opt${pay === 'stripe' ? ' on' : ''}`} onClick={() => setPay('stripe')}>
              <div className="pay-head">
                <div className="rad" /><div className="nm">{t('co.card')}</div><div className="badge">Stripe</div>
              </div>
              {pay === 'stripe' && (
                <div className="pay-body">
                  <div className="pay-note">{t('co.cardNote')}</div>
                </div>
              )}
            </div>

            <div className="pay-opt disabled" aria-disabled="true">
              <div className="pay-head">
                <div className="rad" /><div className="nm">{t('co.promptpay')}</div><div className="badge soon">{t('co.soon')}</div>
              </div>
            </div>

            {error && <div className="errnote">{error}</div>}
            <button className="btn full" style={{ marginTop: 20, padding: 16 }} onClick={submit} disabled={busy || (!isPickup && !quote)}>
              {busy ? t('co.placing') : t('co.place', fmt(grand))}
            </button>
          </div>

          <div className="osum">
            <h3>{t('co.summary')}</h3>
            {cart.map((i) => (
              <div className="li" key={i.key}>
                <span>{i.name}{i.variant ? ' · ' + i.variant : ''} ×{i.qty}</span>
                <b>{fmt(i.unitPrice * i.qty)}</b>
              </div>
            ))}
            <div className="li" style={{ border: 0, paddingTop: 12 }}><span>{t('cart.subtotal')}</span><b>{fmt(sub)}</b></div>
            <div className="li" style={{ border: 0 }}>
              <span>{isPickup ? t('co.pickupRow') : freeElig ? t('co.shipRowFree') : t('co.shipRow')}</span>
              <b>{isPickup ? t('co.freeWord') : quote ? (shipCost ? fmt(shipCost) : t('co.freeWord')) : '…'}</b>
            </div>
            <div className="li" style={{ border: 0, fontSize: 16, marginTop: 6 }}>
              <span style={{ color: 'var(--ink)' }}>{t('co.grand')}</span>
              <b style={{ fontSize: 18 }}>{fmt(grand)}</b>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-2)', marginTop: 14, lineHeight: 1.7 }}>
              {isPickup ? t('co.pickupAt') : t('co.dest', countryName(country, (COUNTRIES.find((c) => c[0] === country) || [])[1]))}<br />
              {t('co.emailNote')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
